/**
 * Single source of truth for tour step COUNTS and tooltip COPY.
 *
 * Adding/removing a step used to mean hand-editing the `tipCount`
 * literal in up to nine files (and the stale TourTooltip auto-resolve
 * constants shipped wrong because of it). Texts were inlined per call
 * site. Both now live here; everything BEHAVIOURAL (anchors, geometry,
 * onNext/onBack callbacks, step numbers) deliberately stays at the
 * call sites — this file is copy, not choreography.
 *
 * The E2E walk script asserts against this same table, so a text edit
 * here updates both the UI and the verification oracle together.
 */

export const TIP_COUNTS = {
  marking: 13,
  draft: 9,
  newTask: 6,
  feedback: 8,
};

export const STEP_TEXT = {
  marking: {
    1: "To view or edit your marking rubrics and preferences, click the 'Marking Tools' page on the left sidebar.",
    2: 'The marking templates are split into different categories based on the task type and level of feedback being provided. This is your marking toolkit.',
    3: 'Rubrics are grade-based marking criteria for extended responses.',
    4: "Comment banks are sets of in-text annotations for specific feedback down the side of a student's response.",
    5: 'A success criteria provides a checklist of elements that you would like students to include in their responses, whether extended responses or practice paragraphs.',
    6: 'Short Answers provide mark-based criteria for short-answer type questions (e.g. 1-8 marks).',
    7: 'Your templates for each feedback type will appear here.',
    8: "Click 'Open' to preview the template.",
    9: 'Check the template details here.',
    10: 'To edit a template, create a duplicate first.',
    11: "Your editable rubric will appear below the template with 'Copy' in the title.",
    12: 'Click New Rubric to add your own rubric into your templates.',
    13: 'Teachers can share custom marking templates with other teachers to save time. Click here to review shared templates from other teachers before adding them into your library.',
  },
  draft: {
    1: "Teachers and students can use the 'My Drafts' page any time to submit practice responses and receive instant feedback (separate to teacher-assigned tasks).",
    2: 'Set up your draft to receive the most relevant feedback.',
    3: 'Add a question and/or stimulus.',
    4: 'Type a response or upload a handwritten response (photo or scanned PDF). Handwritten responses are converted into typed copies that can be edited before submitting.',
    5: "Click the 'Submit Now' button to generate feedback in seconds.",
    6: "JeddAI's in-text annotations sit alongside the line they refer to — scroll through the response to see each one. Click any card to expand the suggested reply.",
    7: 'As a teacher, you can edit the suggested comments, and as a student, you can apply the suggestions directly to the draft.',
    8: 'Depending on the submission type, JeddAI provides overall feedback (editable) in the form of rubrics, success criteria and summary comments.',
    9: 'You can download and/or share a response with students, which includes all of the feedback.',
  },
  // Steps 4-6 follow the INLINE question card's top-to-bottom order
  // (task/submission type dropdowns -> question/instruction + stimulus ->
  // marking rubrics + self-assessment). The card is added by tip 3's Next.
  newTask: {
    1: 'Use the new task button to create a task.',
    2: 'These are all the steps you need go through to complete the task.',
    3: 'Add one or more questions here.',
    4: 'Select the Task Type and Submission Type that best fits your question — these determine the marking rubrics JeddAI suggests.',
    5: 'Write your question or instruction here. You can also add an image, text or a PDF to give context for your question.',
    6: "JeddAI automatically suggests marking rubrics to match your task type — click 'View/edit marking rubrics' any time to review or change them. You can also let students highlight their own work first by adding self-assessment areas.",
  },
  feedback: {
    1: 'Feedback options tabs at the top will help you switch between the different types of feedback.',
    2: 'The Self-assessment tab will display student self assessment. This tab will only appear if self-assessment is enabled in task.',
    3: 'The Specific comments tab is where you add in-text feedback comments for this student response.',
    4: 'Teachers can identify key sections of the response, highlight them and add a comment to it by clicking on the Comment icon.',
    5: 'Add replies to the comment given by JeddAI. JeddAI will give you give you a first draft of comments.',
    6: 'The Summary comments tab is where overall comments about the student’s work can be seen.',
    7: 'The summary comments highlights the strengths and areas of improvement in the student response..',
    8: 'Click the Submit Feedback button to deliver feedback to the student.',
  },
};

// --- Simplified "wizard" tour (non-school teachers + students) ----------------
// Shown on the demo "My Drafts" page after the welcome carousel. Three logical
// steps: (1) an UNCOUNTED prompt on the Submit Now button, then two COUNTED tips
// on the in-text comments and the summary feedback ("Tip N of 2"). Kept separate
// from the school TIP_COUNTS / STEP_TEXT above so the school tours' E2E walk
// oracle and copy are completely unaffected.
export const WIZARD_TIP_COUNT = 2;
export const WIZARD_STEP_TEXT = {
  // Step 1 — uncounted; the prompt copy differs by audience.
  submit: {
    teacher: "Click below to see what happens when you submit a draft (it's magic!)",
    student: 'Click below to see the magic happen!',
  },
  // Step 2 — "Tip 1 of 2".
  comments: 'Your feedback comments are linked to the highlighted text',
  // Step 3 — "Tip 2 of 2".
  summary: 'Summary feedback includes all your marking criteria',
};
